import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import useAuth from "./useAuth";

interface Schedule {
  subject: string;
}

interface Meeting {
  datetime: string; // ISO format
  file_name: string;
  meeting_id: string;
  meeting_type: string;
  schedules: Schedule[];
}

interface AgendaData {
  meetings: Meeting[];
}

interface UseAgendaReturn {
  data: AgendaData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface UseAgendaProps {
  date: string; // YYYY-MM-DD format
}

const useAgenda = ({ date }: UseAgendaProps): UseAgendaReturn => {
  const [data, setData] = useState<AgendaData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { user_id } = useAuth();

  const fetchAgendaData = useCallback(async () => {
    if (!user_id || !date || date === "") {
      setError("Missing user ID or date");
      setIsLoading(false);
      return;
    }

    console.log("Fetching agenda data for:", { date, userId: user_id });
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        "https://oparl-textprovider-production.up.railway.app/calendarSpecificDay",
        {
          params: {
            date,
            userId: user_id,
          },
          timeout: 10000, // 10 second timeout
        }
      );

      console.log("Agenda API response:", response.data);

      // Validate response structure
      if (
        !response.data ||
        !response.data.meetings ||
        !Array.isArray(response.data.meetings)
      ) {
        console.warn(
          "Agenda API returned unexpected data format:",
          response.data
        );
        setData({ meetings: [] });
        return;
      }

      setData(response.data);
      console.log("Agenda data updated:", response.data);
    } catch (err) {
      console.error("Failed to fetch agenda data:", err);
      if (axios.isAxiosError(err)) {
        if (err.code === "ECONNABORTED") {
          setError("Request timeout - please try again");
        } else if (err.response?.status === 404) {
          setError("Agenda service not found");
        } else if (err.response?.status && err.response.status >= 500) {
          setError("Server error - please try again later");
        } else {
          setError(`Failed to load agenda data: ${err.message}`);
        }
      } else {
        setError("Failed to load agenda data");
      }
      setData({ meetings: [] });
    } finally {
      setIsLoading(false);
      console.log("Agenda loading finished");
    }
  }, [user_id, date]);

  useEffect(() => {
    console.log("useAgenda effect triggered", { user_id, date });
    if (!user_id || !date || date === "") {
      setIsLoading(false);
      setError(null);
      setData(null);
      return;
    }
    fetchAgendaData();
  }, [fetchAgendaData, user_id, date]);

  const refetch = () => {
    fetchAgendaData();
  };

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export default useAgenda;
export type { Meeting, Schedule, AgendaData };
